import type { TimeEntry } from "@pentacode/core";
import type { DateString, Hours } from "@pentacode/openapi";
import "@pentacode/ui/components";
import { css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { alert, confirm } from "../../../../elements/alert-dialog";
import { Checkbox } from "../../../../elements/checkbox";
import { DateInput } from "../../../../elements/date-input";
import { Dialog } from "../../../../elements/dialog";
import { DurationInput } from "../../../../elements/duration-input";
import "../../../../elements/select-wage-type";
import type { SelectWageType } from "../../../../elements/select-wage-type";
import { app } from "../../../../init";

@customElement("ptc-hour-adjustment-dialog")
export class HourAdjustmentDialog extends Dialog<TimeEntry | undefined, boolean> {
    readonly preventDismiss = true;

    @query("ptc-date-input[name='date']")
    private _dateInput: DateInput;

    @query("ptc-duration-input[name='hours']")
    private _hoursInput: DurationInput;

    @query("textarea[name='comment']")
    private _commentInput: HTMLTextAreaElement;

    @query("select[name='sign']")
    private _signSelect: HTMLSelectElement;

    @query("ptc-select-wage-type[name='wageType']")
    private _wageTypeSelect: SelectWageType;

    @query("ptc-checkbox-button[name='paid']")
    private _paidButton: Checkbox;

    @query("form")
    private _form: HTMLFormElement;

    @state()
    private _timeEntry: TimeEntry;

    async show(timeEntry?: TimeEntry) {
        const promise = super.show();

        if (timeEntry) {
            this._timeEntry = timeEntry;
            await this.updateComplete;
            this._hoursInput.value = timeEntry.hours ? (Math.abs(timeEntry.hours) as Hours) : null;
            this._signSelect.value = timeEntry.hours && timeEntry.hours > 0 ? "+" : "-";
            this._dateInput.value = timeEntry.date;
            this._paidButton.checked = timeEntry.paid;
            this._wageTypeSelect.value = timeEntry.wageType || "";
            this._commentInput.value = timeEntry.comment || "";
            this._hoursInput.focus();
        }

        await this.updateComplete;
        this.requestUpdate();

        return promise;
    }

    private async _submit(e: FocusEvent) {
        e.preventDefault();

        const entry = this._timeEntry;
        const formData = new FormData(this._form);

        const sign = (formData.get("sign") as string) === "-" ? -1 : 1;
        entry.date = formData.get("date") as DateString;
        entry.hours = (sign * Number(formData.get("hours") as string)) as Hours;
        entry.paid = formData.has("paid");
        entry.comment = formData.get("comment") as string;
        entry.wageType = formData.get("wageType") as string;

        this.loading = true;

        try {
            await app.createOrUpdateTimeEntries(entry, { wait: true, publish: true });
            this.done(true);
        } catch (err) {
            this.hide();
            await alert(err.message, { type: "warning" });
            void this.show();
        }

        this.loading = false;
    }

    private async _delete() {
        this.hide();
        if (
            !(await confirm("Wollen Sie diese Zeitbuchung wirklich löschen?", "Löschen", "Abbrechen", {
                title: "Zeitbuchung Löschen",
                type: "destructive",
            }))
        ) {
            this._show();
            return;
        }
        this._show();

        this.loading = true;

        try {
            await app.removeTimeEntries(this._timeEntry, true);
            this.done(true);
        } catch (err) {
            this.hide();
            await alert(err.message, { type: "warning" });
            this._show();
        }

        this.loading = false;
    }

    private _validateDate(e: Event) {
        const input = e.target as HTMLInputElement;
        const employee = app.getEmployee(this._timeEntry.employeeId!);
        const contract = employee && employee.getContractForDate(input.value);
        input.setCustomValidity(!contract ? "Es existiert kein Vertrag für diesen Zeitraum!" : "");
    }

    static styles = [
        ...Dialog.styles,
        Checkbox.styles,
        DateInput.styles,
        DurationInput.styles,
        css`
            :host {
                --dialog-max-width: 400px;
            }

            form {
                padding: 0 1em;
            }

            .field > label {
                padding: 0.6em 0.4em;
            }

            .amount select {
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
                border-right: none;
            }

            .amount input {
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
            }
        `,
    ];

    renderContent() {
        if (!this._timeEntry) {
            return html``;
        }

        return html`
            <form @submit=${this._submit}>
                <div class="center-aligning margined vertically-padded horizontal layout">
                    <div class="big stretch">${this._timeEntry.id ? "Zeitbuchung Bearbeiten" : "Neue Zeitbuchung"}</div>
                    <button type="button" ?hidden=${!this._timeEntry.id} class="subtle icon" @click=${this._delete}>
                        <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                    </button>
                </div>

                <div class="horizontal spacing layout fields">
                    <div class="field">
                        <label><ptc-ui-icon name="icon.object.date"></ptc-ui-icon>Datum</label>
                        <ptc-date-input
                            name="date"
                            required
                            @change=${(e: Event) => this._validateDate(e)}
                            datePicker="popover"
                        ></ptc-date-input>
                    </div>

                    <div class="field stretch">
                        <label
                            ><ptc-ui-icon name="icon.feature.time-tracking"></ptc-ui-icon>Betrag
                            (Stunden:Minuten)</label
                        >
                        <div class="horizontal layout amount">
                            <select name="sign">
                                <option value="-">Abbuchung</option>
                                <option value="+">Gutschrift</option>
                            </select>
                            <ptc-duration-input name="hours" required class="stretch collapse"></ptc-duration-input>
                        </div>
                    </div>
                </div>

                <div class="field">
                    <label><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon>Kommentar</label>
                    <textarea name="comment" placeholder='z.B. "Auszahlung Überstunden"'></textarea>
                </div>

                <ptc-checkbox-button
                    .label=${html`
                        <ptc-ui-icon name="icon.object.currency"></ptc-ui-icon> Im Lohnbericht Berücksichtigen
                    `}
                    name="paid"
                    id="paidButton"
                    buttonClass="transparent"
                    @change=${() => {
                        this._wageTypeSelect.value = "";
                        this.requestUpdate();
                    }}
                ></ptc-checkbox-button>

                <ptc-ui-tooltip for="paidButton" placement="right">
                    Wählen Sie diese Option, falls die abgebuchten oder gutgeschriebenen Stunden ausbezahlt werden. Die
                    Zeitbuchung wird dann in der Lohnbuchhaltung und in der Personalkostenberechnung berücksichtigt.
                </ptc-ui-tooltip>

                <div class="field" ?hidden=${!this._paidButton?.checked}>
                    <label><ptc-ui-icon name="icon.action.recalculate"></ptc-ui-icon> Lohnart</label>
                    <ptc-select-wage-type name="wageType"></ptc-select-wage-type>
                </div>

                <div class="horizontal evenly stretching spacing layout vertically-margined vertically-padded">
                    <button class="primary">Speichern</button>
                    <button type="button" class="transparent" @click=${() => this.done()}>Abbrechen</button>
                </div>
            </form>
        `;
    }
}
